import { useEffect, useRef, useState } from "react";

type RecorderState = "idle" | "requesting" | "ready" | "recording" | "stopped" | "error";

export function useInterviewRecorder() {
  const [state, setState] = useState<RecorderState>("idle");
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const durationIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (durationIntervalRef.current !== null) {
        window.clearInterval(durationIntervalRef.current);
      }

      previewStream?.getTracks().forEach((track) => track.stop());
    };
  }, [previewStream]);

  // Returns null on success, or an error message string on failure.
  // The message is also stored in recorder.error for display in the recording stage.
  async function prepare(): Promise<string | null> {
    if (!navigator.mediaDevices?.getUserMedia) {
      const msg =
        "Camera and microphone access requires a secure (HTTPS) connection and a supported browser.";
      setError(msg);
      setState("error");
      return msg;
    }

    try {
      setState("requesting");
      setError(null);
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {
            facingMode: { ideal: "user" },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
      } catch (constraintError) {
        // Retry with minimal constraints when the device can't satisfy video preferences
        if (
          constraintError instanceof DOMException &&
          (constraintError.name === "OverconstrainedError" ||
            constraintError.name === "ConstraintNotSatisfiedError")
        ) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        } else {
          throw constraintError;
        }
      }

      // Detect if the OS or browser kills a track mid-session (common on iOS when backgrounded)
      stream.getTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          if (durationIntervalRef.current !== null) {
            window.clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
          }
          setError(
            "Camera or microphone was disconnected. Please reload the page and try again."
          );
          setState("error");
        });
      });

      setPreviewStream(stream);
      setBlob(null);
      setDurationSeconds(0);
      setState("ready");
      return null;
    } catch (caughtError) {
      const domName = caughtError instanceof DOMException ? caughtError.name : null;
      let message: string;
      if (domName === "NotAllowedError" || domName === "PermissionDeniedError") {
        message =
          "Camera and microphone access was blocked. Click the camera icon in your browser's address bar to allow access, then try again.";
      } else if (domName === "NotFoundError" || domName === "DevicesNotFoundError") {
        message =
          "No camera or microphone was detected. Please check that your devices are connected and try again.";
      } else if (domName === "NotReadableError" || domName === "TrackStartError") {
        message =
          "Your camera or microphone is in use by another application. Please close other apps using the camera or microphone and try again.";
      } else {
        message =
          caughtError instanceof Error
            ? caughtError.message
            : "Camera and microphone access is required.";
      }
      setError(message);
      setState("error");
      return message;
    }
  }

  function start() {
    if (!previewStream) {
      return;
    }

    // Reject early if any track has already been stopped by the OS or browser
    if (previewStream.getTracks().some((t) => t.readyState === "ended")) {
      setError("Camera or microphone was disconnected. Please reload the page and try again.");
      setState("error");
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";

    const mediaRecorder = new MediaRecorder(previewStream, {
      mimeType,
      videoBitsPerSecond: 400_000,
      audioBitsPerSecond: 64_000
    });
    mediaRecorderRef.current = mediaRecorder;
    chunksRef.current = [];
    setBlob(null);
    setDurationSeconds(0);

    mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    });

    mediaRecorder.addEventListener("error", () => {
      if (durationIntervalRef.current !== null) {
        window.clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
      setError("Recording stopped unexpectedly. Please use retry to record again.");
      setState("error");
    });

    mediaRecorder.addEventListener("stop", () => {
      const recordedBlob = new Blob(chunksRef.current, { type: mimeType });
      setBlob(recordedBlob);
      setState("stopped");

      if (durationIntervalRef.current !== null) {
        window.clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    });

    mediaRecorder.start(1000);
    setState("recording");
    durationIntervalRef.current = window.setInterval(() => {
      setDurationSeconds((current) => current + 1);
    }, 1000);
  }

  function stop() {
    mediaRecorderRef.current?.stop();
  }

  function reset() {
    setBlob(null);
    setDurationSeconds(0);
    setError(null);
    setState(previewStream ? "ready" : "idle");
  }

  return {
    state,
    previewStream,
    blob,
    durationSeconds,
    error,
    prepare,
    start,
    stop,
    reset
  };
}
